import { useEffect, useState } from "react"
import { Button, Form } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"

import ControlInput from "../../../components/form-inputs/ControlInput"
import LoadingTextSwap from "../../../components/LoadingTextSwap"
import isEmail from "validator/lib/isEmail"
import { isValidAccountType, isValidPassword } from "../../../utils/validateInput"
import { useCheckEmailExistsMutation } from "../../../app/authApiSlice"
import { useAdminCreateAccountMutation } from "../../../app/adminApiSlice"
import SelectInput from "../components/SelectInput"

const CreateAccountPage = () => {
  const navigate = useNavigate()
  const [checkEmailExists] = useCheckEmailExistsMutation()
  const [createAccount] = useAdminCreateAccountMutation()

  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [passwordConfirm, setPasswordConfirm] = useState("")
  const [accountType, setAccountType] = useState("")
  
  const [blurAll, setBlurAll] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  const [valid, setValid] = useState({
    email: false,
    password: false,
    passwordConfirm: false,
    accountType: false,
  })

  const allValid = Object.values(valid).every(Boolean)

  // Clear error message when all fields are valid
  useEffect(() => {
    if (error && allValid) {
      setError("")
    }
  }, [allValid])

  // Clear email error message when email is updated
  useEffect(() => {
    if (error === "Email already in use") {
      setError("")
    }
  }, [email])

  // Function to update valid state
  const updateValid = (key, value) => {
    setValid(prev => ({ ...prev, [key]: value }))
  }

  // Special handler for password to also validate passwordConfirm if it has a value
  const handleSetPassword = (value) => {
    setPassword(value)
    if (value !== passwordConfirm) {
      updateValid("passwordConfirm", false)
    } else {
      updateValid("passwordConfirm", true)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (error || loading) return

    if (!allValid) {
      setBlurAll(true)
      return setError("All fields must be valid")
    }

    setLoading(true)

    // Check if email is already in use
    const { emailExists } = await checkEmailExists(email).unwrap()

    if (emailExists) return setError("Email already in use"), setLoading(false)

    // Create user account
    const createAccountData = {
      email,
      password,
      accountType
    }

    try {
      const { uid } = await createAccount(createAccountData).unwrap()
      toast.success(
        `Account created with membership number ${uid}. This account will not show up in the members list until the user has completed the signup details.`
        ,
        { duration: 12000 }
      )
      navigate("/admin/members")
    } catch (error) {
      console.log(error)
      toast.error("Error creating account")
    }

    setLoading(false)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 my-1">Email</b>
          <div className="w-100">
            <ControlInput
              type="email"
              name="email"
              value={email}
              isValid={valid.email}
              setValid={updateValid}
              setValue={setEmail}
              validTest={isEmail}
              blurAll={blurAll}
              feedback="Please enter a valid email address"
            />
          </div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 my-1">Password</b>
          <div className="w-100">
            <ControlInput
              type="password"
              name="password"
              value={password}
              isValid={valid.password}
              setValid={updateValid}
              setValue={handleSetPassword}
              validTest={isValidPassword}
              blurAll={blurAll}
              feedback="Password must be at least 8 characters long and contain at least one number, one uppercase letter, one lowercase letter and one special character"
            />
          </div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 my-1">Confirm Password</b>
          <div className="w-100">
            <ControlInput
              type="password"
              name="passwordConfirm"
              value={passwordConfirm}
              isValid={valid.passwordConfirm}
              setValid={updateValid}
              setValue={setPasswordConfirm}
              blurAll={blurAll}
              validTest={(newPasswordConfirm) => password === newPasswordConfirm}
              feedback="Passwords do not match"
            />
          </div>
        </div>
        <div className="d-flex flex-column flex-sm-row align-items-center border-bottom p-2 custom-hover-tr">
          <b className="text-muted w-100 my-1">Account Type</b>
          <div className="w-100">
            <SelectInput
              name="accountType"
              isValid={valid.accountType}
              setValid={updateValid}
              setValue={setAccountType}
              validTest={isValidAccountType}
              blurAll={blurAll}
              feedback="Please select an account type"
            />
          </div>
        </div>
      </div>
      {error && <p className="text-danger i text-center fw-semibold mt-3">{error}</p>}
      <Button
        disabled={loading}
        className="px-4 mt-3 mx-auto rounded-3 d-block"
        variant="dark"
        type="submit"
      >
        <LoadingTextSwap isLoading={loading}>
          Create Account
        </LoadingTextSwap>
      </Button>
    </Form>
  )
}

export default CreateAccountPage